import { callIntercom, isMessengerPanelOpen } from "./intercom-sdk";
import { getUnreadCount, isMessengerOpen } from "./messenger-state";

/**
 * Forces Intercom's full app to load so unread counts become knowable.
 *
 * Intercom stays in "lightweight" mode until the messenger is first opened,
 * and the unread count lives in the full app that mode has not loaded — so
 * `onUnreadCountChange` reports 0 forever otherwise, however it is registered.
 *
 * `show()` immediately followed by `hide()` loads the full app with nothing
 * rendered — measured at 0ms of visible panel — and does not mark anything
 * read: reaching a conversation still takes opening the Messages tab and
 * picking one. If a future Intercom release renders before the `hide()` lands
 * the cost is a brief flash, not a broken messenger.
 *
 * It is not free — roughly six extra bundles and an RTM websocket — so it runs
 * once per session, and late enough to stay off the critical path.
 */
export const FULL_MESSENGER_LOAD_DELAY_MS = 2000;
const FULL_MESSENGER_SETTLE_MS = 3000;

let fullMessengerRequested = false;

function openAndClose() {
  // Intercom restores an open messenger across a reload. Closing it again
  // throws the user out of the conversation they left open, and races its
  // session restore hard enough to make it log http_post_session_empty — so
  // never do this while the panel is up. Nothing is lost by skipping: an open
  // messenger means the full app is already loading, which is the entire point
  // of this call.
  if (isMessengerOpen() || isMessengerPanelOpen()) return;
  callIntercom("show");
  callIntercom("hide");
}

export function loadFullMessenger() {
  if (fullMessengerRequested) return;
  fullMessengerRequested = true;
  openAndClose();
  // The first open only bootstraps the app: the unread sync lands after it, so
  // that pass still reports 0. A second open once things have settled is what
  // reports the real count — skipped when the first one already got it.
  setTimeout(() => {
    if (getUnreadCount() === 0) openAndClose();
  }, FULL_MESSENGER_SETTLE_MS);
}

/**
 * A boot drops back to lightweight mode, so the full app has to be pulled in
 * again before the new user's unread count is knowable.
 */
export function resetFullMessengerPreload() {
  fullMessengerRequested = false;
}

/**
 * Runs `callback` at the first idle moment, or at `timeout` at the latest.
 *
 * A fixed delay has to be pessimistic enough for a slow machine, which makes
 * everyone else wait for nothing; idle time is the thing actually being waited
 * for. requestIdleCallback only reached Safari in 16.4, just past our
 * browserslist floor, so the timer is the fallback rather than the plan.
 */
export function whenIdle(callback: () => void, timeout: number) {
  if (typeof window.requestIdleCallback === "function") {
    const handle = window.requestIdleCallback(callback, { timeout });
    return () => window.cancelIdleCallback(handle);
  }
  const handle = setTimeout(callback, timeout);
  return () => clearTimeout(handle);
}
